import React, { useState, useEffect, useRef, useCallback } from 'react';
import './RobotVideo.css';

const VIDEO_RECONNECT_DELAY_MS = 1500;

const RobotVideo = ({ robotId, token, messages }) => {
  const [debugInfo, setDebugInfo] = useState('');
  const [imageKey, setImageKey] = useState(0); // Force refresh when robot changes
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const restartStream = useCallback((message = messages.reconnectingVideo) => {
    if (!robotId || !token) {
      return;
    }

    clearReconnectTimer();
    setImageLoaded(false);
    setImageError(false);
    setDebugInfo(message);
    setImageKey(Date.now());
  }, [clearReconnectTimer, messages.reconnectingVideo, robotId, token]);

  const scheduleReconnect = useCallback(() => {
    clearReconnectTimer();
    reconnectTimerRef.current = setTimeout(() => {
      restartStream(messages.reconnectingVideo);
    }, VIDEO_RECONNECT_DELAY_MS);
  }, [clearReconnectTimer, messages.reconnectingVideo, restartStream]);

  // Manage stream lifecycle when robot changes
  useEffect(() => {
    if (robotId && token) {
      // Start new stream
      restartStream(messages.loadingVideo);
      console.log('Started stream for robot:', robotId);
    } else {
      clearReconnectTimer();
      setImageLoaded(false);
      setImageError(false);
      setDebugInfo(messages.selectRobotVideo);
    }

    // Cleanup on robot change or unmount
    const currentImg = imgRef.current;

    return () => {
      clearReconnectTimer();
      if (currentImg) {
        // Cancel the image loading by removing the src
        currentImg.src = '';
      }
      console.log('Stopped stream for robot:', robotId);
    };
  }, [clearReconnectTimer, messages.loadingVideo, messages.selectRobotVideo, restartStream, robotId, token]);

  useEffect(() => {
    const reconnectWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        restartStream(messages.refreshingVideo);
      }
    };

    const reconnectWhenOnline = () => {
      restartStream(messages.networkRestoredVideo);
    };

    document.addEventListener('visibilitychange', reconnectWhenVisible);
    window.addEventListener('focus', reconnectWhenVisible);
    window.addEventListener('online', reconnectWhenOnline);

    return () => {
      document.removeEventListener('visibilitychange', reconnectWhenVisible);
      window.removeEventListener('focus', reconnectWhenVisible);
      window.removeEventListener('online', reconnectWhenOnline);
    };
  }, [messages.networkRestoredVideo, messages.refreshingVideo, restartStream]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
    setDebugInfo(messages.videoActive);
  };

  const handleImageError = () => {
    setImageLoaded(false);
    setImageError(true);
    setDebugInfo(messages.reconnectingVideo);
    scheduleReconnect();
  };

  const videoUrl = robotId && token
    ? `/robot-control/api/robots/${robotId}/video?token=${token}&t=${imageKey}`
    : '';

  return (
    <div className="robot-video-container">
      <div className="video-content">
        {videoUrl ? (
          <img
            ref={imgRef}
            src={videoUrl}
            alt={`${messages.loadingVideo} ${robotId}`}
            className="video-feed"
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: imageLoaded ? 'block' : 'none' }}
          />
        ) : (
          <div className="video-state">
            {messages.selectRobotVideo}
          </div>
        )}

        {videoUrl && !imageLoaded && !imageError && (
          <div className="video-state video-state-overlay">
            {messages.loadingVideo}
          </div>
        )}

        {imageError && (
          <div className="video-state video-state-overlay video-state-error">
            {messages.videoError}
          </div>
        )}

        {debugInfo && (
          <div className={`video-debug ${imageLoaded ? 'video-debug-active' : ''}`}>
            {imageLoaded && <span className="video-debug-dot" />}
            {debugInfo}
          </div>
        )}
      </div>
    </div>
  );
};

export default RobotVideo;
